import API from 'constants/API';

import React, {useMemo, useState} from 'react';

import {Blob as ActiveStorageBlob, DirectUpload} from 'activestorage';
import Button from 'components/Button/Button';
import {useGlobalState} from 'hooks/useGlobalState';
import {useSetGlobalState} from 'hooks/useSetGlobalState';
import {useNavigate} from 'react-router-dom';
import {setErrorAC} from 'store/app/appSlice';
import {createPostDTO} from 'typescript/dto/postsDto';
import {downloadPost} from 'utils/helpers/downloadPost';
import {getDefaultBackground} from 'utils/helpers/getBackground';
import {getTemplateId} from 'utils/helpers/getTemplateId';
import {isObjectUrl} from 'utils/helpers/isObjectUrl';

import {namePostFile} from '../helpers/namePostFile';
import {TemplatePostTypeIds} from '../postTypes';
import {createPostACS} from '../requestAC';
import {setBackgroundImageAC, setStepAC} from '../slice';
import {getNextStep, getPrevStep} from '../utils/steps/steps';

import './PostGeneratorSteps.scss';

interface IProps {
  postRefs: React.MutableRefObject<HTMLDivElement[]>;
}

export default function PostGeneratorSteps({postRefs}: IProps) {
  const [isLoading, setLoading] = useState(false);

  const dispatch = useSetGlobalState();

  const navigate = useNavigate();
  const {step, fields, activeTemplate, postType, backgroundImage, postThumbnailFitType} = useGlobalState(
    (state) => state.postGenerator,
  );
  const {managedPub} = useGlobalState((state) => state.managedPub);

  const pubBeers = useGlobalState((state) => state.beersManager.pubBeers);

  const prevStep = () => {
    if (step === 'MANAGE_BEERS') {
      if (backgroundImage) {
        if (isObjectUrl(backgroundImage)) URL.revokeObjectURL(backgroundImage);
        dispatch(setBackgroundImageAC(''));
      }

      navigate(-1);
    } else {
      const nextStep = getPrevStep(step);
      dispatch(setStepAC(nextStep));
    }
  };

  const handleDownload = async () => {
    setLoading(true);
    const downloadErrorMsg = 'There was an issue while downloading your post.';

    if (!managedPub) {
      dispatch(setErrorAC(downloadErrorMsg));
      return;
    }

    try {
      const bgImagePath = backgroundImage || getDefaultBackground(activeTemplate);

      const response = await fetch(bgImagePath);
      const bgImageBlob = await response.blob();
      const bgImageFile = new File([bgImageBlob], bgImagePath, {type: bgImageBlob.type});

      const upload = new DirectUpload(bgImageFile, API.ACTIVE_STORAGE);

      upload.create((error: Error, blob: ActiveStorageBlob) => {
        if (error) {
          dispatch(setErrorAC(downloadErrorMsg));
          return;
        }

        const fileName = namePostFile({postType, pubName: managedPub.attributes.name});

        const titleField = fields.find((field) => field.id === 'title');
        const subtitleField = fields.find((field) => field.id === 'subTitle');

        const createPostPayload: createPostDTO = {
          pub_id: managedPub.id,
          title: titleField?.value || '',
          description: subtitleField?.value || '',
          social_network_kind: TemplatePostTypeIds[postType],
          favorite: false,
          post_thumbnail: blob.signed_id,
          post_thumbnail_fit_type: postThumbnailFitType,
          template_id: getTemplateId(activeTemplate),
          title_font_size: titleField?.style.fontSize || 14,
          description_font_size: subtitleField?.style.fontSize || 14,
        };

        downloadPost({fileName, postRefs, setLoading});

        dispatch(createPostACS(createPostPayload));
      });
    } catch {
      dispatch(setErrorAC(downloadErrorMsg));
    }
  };

  const nextStep = () => {
    const nextStep = getNextStep(step);
    dispatch(setStepAC(nextStep));
  };

  const isTapEmpty = useMemo(
    () => pubBeers.length === 0 || pubBeers.every((beer) => !beer.attributes.on_tap),
    [pubBeers],
  );

  return (
    <div className="post-generator-steps">
      <Button variant="tertiary-outlined" onClick={prevStep}>
        Back
      </Button>
      {step !== 'SELECT_POST_TEMPLATE' ? (
        <Button variant="tertiary" onClick={nextStep} disabled={isTapEmpty}>
          Next Step
        </Button>
      ) : (
        <Button onClick={handleDownload} variant="tertiary">
          {isLoading ? <div className="spinner-border" role="status" /> : 'Download Post'}
        </Button>
      )}
    </div>
  );
}
